import React, { memo, useState, useCallback, useEffect } from "react";
import { Grid, IconButton } from "@mui/material";
import { AutocompleteComponent } from "components/Autocomplete/AutocompleteComponent";
import { ActionsButtons } from "components/CustomButtons/ActionsButtons";
import { useForm } from "hooks/useForm";
import RegularButton from "components/CustomButtons/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CardTable from "components/CardTable/CardTable";
import { api_post, api_get, api_delete, PROYECT } from "services/api";
import { useSearch } from "hooks/useSearch";
import { useFetchLocal } from "hooks/fetch-hooks/useFetchLocal";
import { blue, grey } from "@mui/material/colors";

const BASE_URL = `${process.env.REACT_APP_URL}${PROYECT}`;

const columns = [
  { Header: "Cliente", accessor: "label" },
  {
    Header: "Acciones",
    accessor: "actions",
    Cell: ({ original }) => (
      <ActionsButtons
        buttons={[
          {
            Icon: DeleteIcon,
            color: 3,
            tooltip: "Eliminar",
            onClick: () => original.onRemove(original),
          },
        ]}
      />
    ),
  },
];

const fetchLinkedCustomers = (idUnit) =>
  api_get(`${BASE_URL}/unit-wholesaler-customers?idUnit=${idUnit}`);

const searchCustomers = (filter) =>
  api_get(`${BASE_URL}/customers/wholesalers?filter=${filter}`);

const submitCustomers = (idUnit, idCustomers) =>
  api_post(`${BASE_URL}/unit-wholesaler-customers`, { idUnit, idCustomers });

export const LinkUserCustomer = memo(({ data, onClose }) => {
  const { values, setValues } = useForm({ customersOpt: [], selected: null, searchTerm: "" });
  const [linkedCustomers, setLinkedCustomers] = useState([]);
  const [focused, setFocused] = useState(false);
  const { setSnackbar } = useSearch();

  const [[linkedResp, linkedLoading], fetchLinked] = useFetchLocal(fetchLinkedCustomers);
  const [[searchResp, searchLoading], doSearch] = useFetchLocal(searchCustomers);
  const [[, submitLoading, submitSuccess], doSubmit] = useFetchLocal(
    submitCustomers,
    "Clientes vinculados correctamente"
  );

  useEffect(() => {
    fetchLinked(data.id);
  }, [data.id]);

  useEffect(() => {
    if (linkedResp?.customers) {
      setLinkedCustomers(
        linkedResp.customers.map((c) => ({
          ...c,
          label: c.customerName,
          value: c.idCustomer,
        }))
      );
    }
  }, [linkedResp]);

  useEffect(() => {
    if (searchResp) {
      const opts = (searchResp || []).map((c) => ({
        ...c,
        idCustomer: c.id,
        value: c.id,
        label: `${c.name || ""} ${c.lastname || ""}`.trim(),
      }));
      setValues({ customersOpt: opts });
    }
  }, [searchResp]);

  useEffect(() => {
    if (submitSuccess) {
      setSnackbar("Clientes vinculados correctamente", "success");
      onClose();
    }
  }, [submitSuccess]);

  const handleSearch = useCallback(
    (e) => {
      e?.preventDefault?.();
      if (!values.searchTerm || values.searchTerm.length < 3) return;
      doSearch(values.searchTerm);
    },
    [values.searchTerm]
  );

  const optCount = values.customersOpt?.length || 0;
  const labelText = optCount > 0 ? `Buscar cliente (${optCount} coincidencias)` : "Buscar cliente";

  const handleSelect = useCallback(
    (value) => {
      if (!value) return;
      const exists = linkedCustomers.find((c) => c.idCustomer === value.idCustomer);
      if (!exists) {
        setLinkedCustomers((prev) => [...prev, { ...value, isNew: true }]);
      }
      setValues({ selected: null });
    },
    [linkedCustomers]
  );

  const handleRemove = useCallback(async (customer) => {
    if (customer.id && !customer.isNew) {
      try {
        await api_delete(`${BASE_URL}/unit-wholesaler-customers/${customer.id}`);
        setSnackbar("Cliente desvinculado", "success");
      } catch (e) {
        setSnackbar(e || "Error al desvincular");
        return;
      }
    }
    setLinkedCustomers((prev) => prev.filter((c) => c.idCustomer !== customer.idCustomer));
  }, []);

  const handleSubmit = useCallback(() => {
    const newCustomers = linkedCustomers.filter((c) => c.isNew);
    if (newCustomers.length === 0) return;
    doSubmit(data.id, newCustomers.map((c) => c.idCustomer));
  }, [linkedCustomers, data]);

  const hasNewCustomers = linkedCustomers.some((c) => c.isNew);

  return (
    <>
      <Grid item xs={12}>
        <AutocompleteComponent
          options={values.customersOpt}
          value={values.selected}
          label={labelText}
          name="customer"
          search={values.searchTerm}
          customAction={(v) => handleSelect(v)}
          disabled={searchLoading}
          open={focused && optCount > 0}
          onOpen={() => setFocused(true)}
          onClose={() => setFocused(false)}
          textFieldProps={{
            onChange: ({ target }) => setValues({ searchTerm: target.value }),
            onFocus: () => setFocused(true),
            onBlur: () => setFocused(false),
            onKeyDown: (e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch(e);
              }
            },
          }}
          onAction={
            <>
              {(values.searchTerm || values.selected) && (
                <IconButton
                  size="small"
                  onClick={() => setValues({ searchTerm: "", selected: null, customersOpt: [] })}
                >
                  <ClearIcon sx={{ fontSize: 18, color: grey[500] }} />
                </IconButton>
              )}
              <RegularButton
                type="button"
                justIcon
                round
                size="sm"
                color="transparent"
                onClick={handleSearch}
                disabled={searchLoading}
                loading={searchLoading}
              >
                <SearchIcon sx={{ color: blue[700] }} />
              </RegularButton>
            </>
          }
          noOptionsText="No se encontraron clientes"
          size="small"
        />
      </Grid>
      <Grid item xs={12}>
        <CardTable
          columns={columns}
          data={linkedCustomers.map((item) => ({ ...item, onRemove: handleRemove }))}
          noDataText="Sin clientes asignados"
          pageSize={10}
          loading={linkedLoading}
        />
      </Grid>
      <Grid item xs={12} style={{ display: "flex", justifyContent: "flex-end" }}>
        <RegularButton
          color="success"
          onClick={handleSubmit}
          disabled={!hasNewCustomers}
          loading={submitLoading}
        >
          Guardar
        </RegularButton>
      </Grid>
    </>
  );
});
